import {setFeedbackModalIsActive} from '../../../stores/feedback'
import {PenIcon} from '../../../icons'
import text from '../../../text.json'
import Feedback from '../../core/feedback/feedback'
import {useDispatch, useSelector} from 'react-redux'
import InfoLine from '../InfoLine'
import Sidebar from '../sidebar'
import ContentLoader from '../../core/loader'
import Alert from '../../core/alert'
import {VoucherModal} from './modals'
import SubscriptionSession from './SubscriptionSession'
import {useEffect, useState} from 'react'
import {resetQuery} from '../../../stores/monitoring'
import Authorization from './Authorization'
import {routes} from '../../../routes'
import {getBoolValueFromEnv} from '../../../utils/env'
import Modal from '../../core/modal'
import {setDisableTutorial, setVoucherData, setVoucherModalIsActive} from '../../../stores/voucher'
import {Link, useLocation} from 'react-router-dom'
import localStorageKeys from '../../../utils/localStorageKeys'
import DateHelper from '../../../utils/date'
import {logCustomerIsActive} from '../../../stores/customer-settings'
import classNames from 'classnames'
import layoutText from './text.json'
import moment from 'moment'
import localStorageManager from '../../../utils/localStorageManager'
import plans from '../../../data/plans.json'
import TextHelper from '../../../utils/text'
import limitUsageManager from '../../../utils/hooks/limitUsageManager'
import usageLimitsData from '../../../data/usageLimits.json'

const START_TRIAL = 'or start a trial period '
const DATA = 'data'

const MainLayout = ({item}) => {
    const {title} = item
    const isFirstLogin = useSelector(state => state.resources.isFirstLogin)
    const modalIsActive = useSelector(state => state.voucher.modalIsActive)
    const globalInfo = useSelector(state => state.sidebar.globalInfo)
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated)
    const getMonitoringData = useSelector(state => state.monitoring.getData)
    const subscriptions = useSelector(state => state.payment.subscriptions)
    const lastActiveDay = localStorage.getItem(localStorageKeys.LAST_ACTIVE_DAY)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const dispatch = useDispatch()
    const location = useLocation()
    const plan = localStorage.getItem(localStorageKeys.PLAN)
    const trial = localStorageManager.get(localStorageKeys.TRIAL)
    const now = new Date()
    const isTrialStarted = trial && now > new Date(trial?.started)
    const remainingTrialDay =  moment(trial?.expires).diff(moment(now), 'days')
    const isExpired =  moment(now).isAfter(moment(trial?.expires))
    const activeSubscription = subscriptions?.data?.subscriptions?.find(subscription => !!subscription?.active)
    const noPlan = plan === plans.free_v2.key || (plan === plans.free_v1.key && isTrialStarted && !isExpired)
    const organisationLimitUsages = useSelector(state => state.auth.organisationLimitUsages)
    const [trialMsg, setTrialMsg] = useState('')

    const usageLimitsAdvanced = limitUsageManager(usageLimitsData.limit_modules.monitoring.module, usageLimitsData.limit_keys.server_advanced_resource_count.limit_key, organisationLimitUsages)

    const isFeedbackButtonAllowed = path => {
        if (
            getBoolValueFromEnv('REACT_APP_FEEDBACK_BUTTON_ENABLED') &&
            !isFirstLogin &&
            !routes.monitoring.isSame(path)
        ) {
            return true
        }
    }

    const closeModal = () => {
        dispatch(setVoucherModalIsActive(!modalIsActive))
        dispatch(setDisableTutorial(false))
        dispatch(setVoucherData({}))
    }

    const setWarningMessage = () => {
        if (!isTrialStarted || (isExpired && plan !== plans.free_v2.key) || (!!activeSubscription && !activeSubscription?.in_trial) || plan.includes(plans.appsumo_key)) {
            setTrialMsg(null)
            return
        }

        if (isExpired) {
            setTrialMsg(layoutText.trial_expired_info)
            return
        }

        remainingTrialDay === 0
            ? setTrialMsg(layoutText.trial_ends_today)
            : setTrialMsg(TextHelper.replaceAllChars(layoutText.trial_info, ':number', remainingTrialDay))
    }

    useEffect(() => {
        document.title = `${text.ping_home_console} | ${title}`
        window.scrollTo(0, 0)
        getMonitoringData && dispatch(resetQuery())
    }, [title])

    useEffect(() => {
        const makeAction = async() => {
            const success = await dispatch(logCustomerIsActive())
            if (success) {
                localStorage.setItem(localStorageKeys.LAST_ACTIVE_DAY, DateHelper.convertToStandartDate())
            }
            return
        }

        if (isAuthenticated && (!lastActiveDay || lastActiveDay !== DateHelper.convertToStandartDate())) {
            makeAction()
        }
    }, [isAuthenticated, location?.pathname])

    useEffect(() => {
        if (DATA in subscriptions) {
            setWarningMessage()
        }
    }, [subscriptions])

    const noPlanFreeV2Text = TextHelper.replaceAllChars(layoutText.no_plan_free_v2, ':start_trial', isExpired ? '' : START_TRIAL)
    const isUsageMetricsExceeded = usageLimitsAdvanced?.usage > usageLimitsAdvanced?.value && plan !== plans.free_v2.key && routes.server_resource.containsId(location?.pathname?.split('/'))

    return (
        <>
            {isUsageMetricsExceeded && (
                <div className='plans-info exceeded-metrics'>
                    <span className='plans-info-text'>
                    You have exceeded your advanced metrics limit. To avoid automatic adjustments, please
                        <Link to={routes.plans.path}> upgrade your plan</Link> or disable metrics in your server resource settings.
                    </span>
                </div>
            )}
            {noPlan && (
                <div className='plans-info'>
                    <span className='plans-info-text'>
                        {plan === plans.free_v2.key ? noPlanFreeV2Text : layoutText.no_plan_free_v1}
                        <Link to={routes.plans.path}>here</Link>.
                    </span>
                </div>
            )}

            <Alert/>
            <Modal isOpen={modalIsActive} className={classNames('voucher-modal', {
                dark: isDarkMode
            })} closeModal={closeModal}>
                <VoucherModal closeModal={closeModal}/>
            </Modal>
            <SubscriptionSession/>
            <Authorization/>
            {isFeedbackButtonAllowed(item.path) && (
                <>
                    <div className="feedback-btn">
                        <span className="button"
                            onClick={() => dispatch(setFeedbackModalIsActive(true))}>
                            <PenIcon/>
                            <span className="text">{text.feedback}</span>
                        </span>
                    </div>
                    <Feedback/>
                </>
            )}
            {globalInfo && <InfoLine infoText={globalInfo}/>}
            <div className="container" style={{minHeight: window.innerHeight}}>
                <Sidebar noPlan={noPlan || isUsageMetricsExceeded} trialMessage={trialMsg}/>
                {isAuthenticated ? <item.component/> : <ContentLoader/>}
            </div>
        </>
    )
}

export default MainLayout
